// src/NewUPT/Register/PatientRegistration.jsx
import React, { useState, useRef } from "react";
import patientInfoService from "../patientInfo/patientInfoService";
import PointsBankAPI from "../points/PointsBankAPI";
import PatientContactPermissionService from "../patientInfo/PatientContactPermissionService";
import CaptchaGate from "../Security/CaptchaGate";
import "../shared/PageWrapper.css";

const MIN_FILL_MS = 3000;

function getCookie(name) {
  const m = document.cookie.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
  return m ? decodeURIComponent(m.pop()) : "";
}

export default function PatientRegistration() {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    firstName: "",
    lastName: "",
    dob: "",
    age: "",
    phoneNumber: "",
    email: "",
    sex: "",
    notes: "",
    visits: 0,
    nfWc: 0,
    totalPoints: 0,
  });

  const [permissions, setPermissions] = useState({
    allowPromotionText: false,
    allowPromotionEmail: false,
    allowAppointmentReminderText: false,
    allowAppointmentReminderEmail: false,
    allowAuthorizationReminderText: false,
    allowAuthorizationReminderEmail: false,
  });

  const [submitting, setSubmitting] = useState(false);

  const [hp, setHp] = useState("");
  const formStartRef = useRef(Date.now());

  const [challenge, setChallenge] = useState(null);
  const waiterRef = useRef({ resolve: null });
  const [pendingRegisterRequest, setPendingRegisterRequest] = useState(null);

  const patientService = new patientInfoService();
  const pointsBank = new PointsBankAPI();

  const calculateAge = (dob) => {
    if (!dob) return "";

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [id]: value,
      ...(id === "dob" ? { age: calculateAge(value) } : {}),
    }));
  };

  const buildRequests = () => {
    const cleanUsername = formData.username.trim();
    const cleanPassword = formData.password.trim();
    const cleanEmail = formData.email.trim();

    const detailsRequest = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      dateOfBirth: formData.dob,
      age: calculateAge(formData.dob),
      visits: 0,
      nfWc: 0,
      sex: formData.sex.trim(),
      notes: formData.notes.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      email: cleanEmail,
    };

    const registerRequest = {
      username: cleanUsername,
      password: cleanPassword,
      email: cleanEmail,
    };

    return { registerRequest, detailsRequest };
  };

  const normalizeChallenge = (json) => json?.challenge ?? json ?? null;

  const requestNewChallenge = async () => {
    const res = await fetch("/api/patientinfo/captcha/new", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ appKey: "default-api" }),
    });

    if (!res.ok) {
      throw new Error("Failed to get captcha");
    }

    const json = await res.json();
    return normalizeChallenge(json);
  };

  const ensureHumanPass = async () => {
    if (getCookie("HPASS")) return;

    const ch = await requestNewChallenge();
    setChallenge(ch);

    await new Promise((resolve) => {
      waiterRef.current.resolve = resolve;
    });

    await new Promise((resolve) => setTimeout(resolve, 50));
  };

  const onCaptchaPassed = async () => {
    setChallenge(null);

    waiterRef.current?.resolve?.();
    waiterRef.current.resolve = null;

    if (pendingRegisterRequest) {
      setSubmitting(true);

      try {
        const { registerRequest, detailsRequest } = pendingRegisterRequest;
        await doRegisterFlow(registerRequest, detailsRequest);
        setPendingRegisterRequest(null);
      } catch {
        alert("Please try submitting again.");
      } finally {
        setSubmitting(false);
      }
    }
  };

  const onRefreshChallenge = async () => {
    try {
      const ch = await requestNewChallenge();
      setChallenge(ch);
    } catch {
      // Silent refresh failure. User can submit again.
    }
  };

  const storePatientLogin = (loginResult, fallbackPatientId, fallbackUsername) => {
    if (!loginResult?.token) {
      throw new Error("Patient login did not return a token.");
    }

    const token = loginResult.token;
    const patientId = loginResult.patientId || fallbackPatientId;
    const username = loginResult.username || fallbackUsername;
    const role = loginResult.role || "ROLE_PATIENT";

    if (!patientId) {
      throw new Error("Patient login did not return a patient ID.");
    }

    localStorage.setItem("jwt", token);
    localStorage.setItem("role", role);
    localStorage.setItem("username", username);
    localStorage.setItem("patientId", String(patientId));

    sessionStorage.setItem("sessionExpires", String(Date.now() + 30 * 60 * 1000));

    window.dispatchEvent(new Event("userUpdated"));

    return {
      token,
      patientId,
      username,
      role,
    };
  };

  const doRegisterFlow = async (registerRequest, detailsRequest) => {
    const registrationResponse = await patientService.registerCredentials(
      registerRequest
    );

    if (!registrationResponse?.patientId) {
      throw new Error("Invalid patient ID returned from registration.");
    }

    const patientId = registrationResponse.patientId;

    /*
      Important:
      Login as the newly registered patient right away.
      This makes the JWT patientId match localStorage.patientId,
      which fixes patient-locked features like simple appointments.
    */
    const patientLoginResult = await patientService.login({
      username: registerRequest.username,
      password: registerRequest.password,
    });

    const storedLogin = storePatientLogin(
      patientLoginResult,
      patientId,
      registerRequest.username
    );

    const patientToken = storedLogin.token;

    await patientService.savePatientDetails(
      patientId,
      registerRequest.username,
      detailsRequest,
      patientToken
    );

    await pointsBank.addPointsEntry({
      patientId,
      totalPoints: 0,
    });

    await pointsBank.addFromAppointment(patientId, 5);

    const contactService = new PatientContactPermissionService();

    await contactService.add({
      patientId,
      email: detailsRequest.email,
      phone: detailsRequest.phoneNumber,
      ...permissions,
    });

    alert("Registration successful. You've earned 5 welcome points!");
    window.location.href = "/dashboard/patient";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const elapsed = Date.now() - formStartRef.current;

    if (hp && hp.length > 0) {
      alert("Submission rejected.");
      return;
    }

    if (elapsed < MIN_FILL_MS) {
      alert("Please review your details and try again.");
      return;
    }

    setSubmitting(true);

    try {
      await ensureHumanPass();

      const { registerRequest, detailsRequest } = buildRequests();

      try {
        await doRegisterFlow(registerRequest, detailsRequest);
      } catch (err) {
        const data = err?.response?.data || err?.data || {};

        if (
          err?.status === 403 &&
          data?.action === "captcha_required" &&
          data?.challenge
        ) {
          setPendingRegisterRequest({ registerRequest, detailsRequest });
          setChallenge(normalizeChallenge(data.challenge));
          return;
        }

        const msg = err?.message || String(err);

        if (msg.includes("Email is already registered")) {
          alert("That email is already in use. Please use a different one.");
        } else if (msg.includes("Username already exists")) {
          alert("That username is already taken. Please choose another.");
        } else {
          alert("Error: " + msg);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pageWrapper">
      <div className="formContainer">
        {challenge && (
          <div
            style={{
              position: "fixed",
              left: "50%",
              bottom: 16,
              transform: "translateX(-50%)",
              zIndex: 1000,
              maxWidth: 360,
              width: "calc(100% - 32px)",
              boxShadow: "0 12px 30px rgba(0,0,0,0.24)",
              borderRadius: 14,
              background: "white",
              padding: 10,
            }}
            aria-live="polite"
            aria-label="Security check"
          >
            <CaptchaGate
              challenge={challenge}
              onPassed={onCaptchaPassed}
              onRefresh={onRefreshChallenge}
            />
          </div>
        )}

        <form id="registrationForm" onSubmit={handleSubmit} autoComplete="off">
          <div
            style={{ position: "absolute", left: "-99999px" }}
            aria-hidden="true"
          >
            <label htmlFor="hp">Leave blank</label>
            <input
              id="hp"
              name="hp"
              type="text"
              value={hp}
              onChange={(e) => setHp(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {[
            {
              type: "text",
              id: "username",
              placeholder: "Enter Username",
              label: "Your unique username.",
            },
            {
              type: "password",
              id: "password",
              placeholder: "Enter Password",
              label: "Use at least 8 characters.",
            },
            {
              type: "text",
              id: "firstName",
              placeholder: "Enter First Name",
              label: "Your first name.",
            },
            {
              type: "text",
              id: "lastName",
              placeholder: "Enter Last Name",
              label: "Your last name.",
            },
            {
              type: "date",
              id: "dob",
              placeholder: "Enter Date of Birth",
              label: "Format: YYYY-MM-DD",
            },
            {
              type: "tel",
              id: "phoneNumber",
              placeholder: "Enter Phone Number",
              label: "Provide a valid phone number.",
            },
            {
              type: "email",
              id: "email",
              placeholder: "Enter Email",
              label: "Provide a valid email.",
            },
          ].map(({ type, id, placeholder, label }) => (
            <div className="input-container" key={id}>
              <input
                type={type}
                id={id}
                placeholder={placeholder}
                value={formData[id]}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              <span className="tooltip">{label}</span>
            </div>
          ))}

          <div className="input-container">
            <select id="sex" value={formData.sex} onChange={handleChange} required>
              <option value="" disabled>
                Select Sex
              </option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
            <span className="tooltip">Choose gender identity.</span>
          </div>

          <div className="input-container">
            <textarea
              id="notes"
              placeholder="Enter Notes (Optional)"
              rows={3}
              value={formData.notes}
              onChange={handleChange}
            />
            <span className="tooltip">
              Additional information about the patient.
            </span>
          </div>

          <div className="checkbox-group">
            <label>
              <strong>Contact Preferences:</strong>
            </label>

            {[
              {
                title: "Promotions/Messages",
                ids: ["allowPromotionText", "allowPromotionEmail"],
                description:
                  "Agree to receive promotional SMS or emails. You can disable anytime in dashboard.",
              },
              {
                title: "Appointment Reminders",
                ids: [
                  "allowAppointmentReminderText",
                  "allowAppointmentReminderEmail",
                ],
                description:
                  "Agree to receive appointment reminders via SMS or email.",
              },
              {
                title: "Authorization Notifications",
                ids: [
                  "allowAuthorizationReminderText",
                  "allowAuthorizationReminderEmail",
                ],
                description:
                  "Agree to receive insurance authorization reminders.",
              },
            ].map(({ title, ids, description }) => (
              <div key={title} className="checkbox-row">
                {ids.map((id) => (
                  <div key={id} className="checkbox-column">
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        id={id}
                        checked={permissions[id]}
                        onChange={(e) =>
                          setPermissions((prev) => ({
                            ...prev,
                            [id]: e.target.checked,
                          }))
                        }
                      />
                      <label htmlFor={id}>
                        {id.includes("Text")
                          ? `${title} via text`
                          : `${title} via email`}
                      </label>
                    </div>
                  </div>
                ))}

                <div className="consent-description">
                  <p>{description}</p>
                </div>
              </div>
            ))}
          </div>

          <button type="submit" disabled={submitting}>
            {submitting ? "Registering..." : "Register"}
          </button>
        </form>
      </div>
    </div>
  );
}